/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useAuthContext } from "../../context/auth";
import { getDolarFallback, getDolarLastDate, loadExchangeRates } from "../../utils/exchangeRateService";
import { getCuentasBancarias } from "../../utils/movimientosBancarios";
import { client } from "../../utils/supabase";
import { Header } from "../Header";
import styles from "./DashboardFinanciero.module.scss";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto, moneda = "CLP") => {
  if (monto == null) return "-";
  const num = Number(monto);
  if (moneda === "USD") return `US$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
  return `$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFechaHora = (fechaStr) => {
  if (!fechaStr) return "Nunca";
  const d = new Date(fechaStr);
  return `${d.toLocaleDateString("es-CL")} ${d.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`;
};

// ─── Componente principal ────────────────────────────────

const DashboardFinanciero = () => {
  const { pais } = useAuthContext();

  const [cuentas, setCuentas] = useState([]);
  const [alertas, setAlertas] = useState({ movPendientes: 0, dtesPendientes: 0 });
  const [proyeccion, setProyeccion] = useState(null);
  const [proyeccion90, setProyeccion90] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dolarHoy, setDolarHoy] = useState(0);
  const [dolarFecha, setDolarFecha] = useState(null);

  const cargarDatos = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      // Cargar exchange rates si no están cargadas
      await loadExchangeRates();
      setDolarHoy(getDolarFallback(pais));
      setDolarFecha(getDolarLastDate(pais));

      // Cargar cuentas bancarias + alertas en paralelo
      const [ctas, movPend, dtesEmPend, dtesRecPend] = await Promise.all([
        getCuentasBancarias(pais),
        client
          .from("movimientos_bancarios")
          .select("id", { count: "exact", head: true })
          .eq("pais", pais)
          .eq("estado", "pendiente"),
        client
          .from("facturas_sii_emitidas")
          .select("id", { count: "exact", head: true })
          .eq("pais", pais)
          .eq("estado_vinculacion", "pendiente"),
        client
          .from("facturas_sii_recibidas")
          .select("id", { count: "exact", head: true })
          .eq("pais", pais)
          .eq("estado_vinculacion", "pendiente"),
      ]);

      setCuentas(ctas);
      setAlertas({
        movPendientes: movPend.count || 0,
        dtesPendientes: (dtesEmPend.count || 0) + (dtesRecPend.count || 0),
        dtesEmitidas: dtesEmPend.count || 0,
        dtesRecibidas: dtesRecPend.count || 0,
      });

      // ─── Proyección fin de mes ───────────────────────────
      const now = new Date();
      const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const primerDia = `${mesActual}-01`;
      const ultimoDia = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const ultimoDiaStr = `${mesActual}-${ultimoDia.getDate()}`;

      // Meses anteriores para promedio gastos recurrentes (3 meses)
      const mesesAnteriores = [];
      for (let i = 1; i <= 3; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        mesesAnteriores.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
      }
      const fechaDesde3m = `${mesesAnteriores.at(-1)}-01`;

      const [ingPendRes, egrCompRes, gastosRecRes] = await Promise.all([
        // Ingresos pendientes de cobro: SII emitidas vinculadas, del mes, sin cobrar
        client
          .from("facturas_sii_emitidas")
          .select("monto_total")
          .eq("pais", pais)
          .eq("estado_vinculacion", "vinculada")
          .gte("fecha_emision", primerDia)
          .lte("fecha_emision", ultimoDiaStr),
        // Egresos comprometidos: facturas_proveedor pendientes o parciales del mes
        client
          .from("facturas_proveedor")
          .select("monto_total, monto_pagado")
          .eq("pais", pais)
          .in("estado", ["pendiente", "parcial"])
          .gte("fecha", primerDia)
          .lte("fecha", ultimoDiaStr),
        // Gastos recurrentes: promedio últimos 3 meses
        client
          .from("movimientos_caja")
          .select("monto, fecha")
          .eq("pais", pais)
          .eq("tipo", "pago")
          .eq("categoria_pago", "gasto_recurrente")
          .neq("estado", "anulado")
          .gte("fecha", fechaDesde3m),
      ]);

      const ingresosPendientes = (ingPendRes.data || []).reduce((s, f) => s + Number(f.monto_total || 0), 0);

      const egresosComprometidos = (egrCompRes.data || []).reduce((s, f) => {
        const pendiente = Number(f.monto_total || 0) - Number(f.monto_pagado || 0);
        return s + Math.max(0, pendiente);
      }, 0);

      const gastosRecData = gastosRecRes.data || [];
      const mesesConGastos = new Set(gastosRecData.map((g) => g.fecha?.substring(0, 7))).size || 1;
      const totalGastosRec = gastosRecData.reduce((s, g) => s + Number(g.monto || 0), 0);
      const gastosRecurrentes = Math.round(totalGastosRec / mesesConGastos);

      const saldoActualCLP = Number(ctas.find((c) => c.moneda === "CLP")?.saldo_actual || 0);
      const saldoProyectado = saldoActualCLP + ingresosPendientes - egresosComprometidos - gastosRecurrentes;

      setProyeccion({ ingresosPendientes, egresosComprometidos, gastosRecurrentes, saldoProyectado });

      // ─── Proyección 90 días ──────────────────────────────
      const meses90 = [];
      for (let i = 1; i <= 3; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const y = d.getFullYear();
        const m = d.getMonth() + 1;
        const ultimo = new Date(y, m, 0).getDate();
        meses90.push({
          label: `${["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"][m - 1]} ${y}`,
          desde: `${y}-${String(m).padStart(2, "0")}-01`,
          hasta: `${y}-${String(m).padStart(2, "0")}-${ultimo}`,
        });
      }

      // Cargar datos para los 3 meses en paralelo
      const [siiEm90Res, fp90Res, pipeline90Res, pipelineContratos90Res] = await Promise.all([
        // Facturas SII emitidas pendientes de cobro para los 3 meses
        client
          .from("facturas_sii_emitidas")
          .select("monto_total, fecha_emision")
          .eq("pais", pais)
          .eq("estado_vinculacion", "vinculada")
          .gte("fecha_emision", meses90[0].desde)
          .lte("fecha_emision", meses90[2].hasta),
        // Facturas proveedor pendientes para los 3 meses
        client
          .from("facturas_proveedor")
          .select("monto_total, monto_pagado, fecha")
          .eq("pais", pais)
          .in("estado", ["pendiente", "parcial"])
          .gte("fecha", meses90[0].desde)
          .lte("fecha", meses90[2].hasta),
        // Pipeline: oportunidades (sales) con estatus >= 50% y fecha_cierre en los 3 meses
        client
          .from("sales")
          .select("importe, fecha_cierre, estatus")
          .eq("pais", pais)
          .eq("eliminada", false)
          .gte("fecha_cierre", meses90[0].desde)
          .lte("fecha_cierre", meses90[2].hasta),
        // Pipeline: contratos (sales_contratos) con estatus >= 50% y fecha_cierre en los 3 meses
        client
          .from("sales_contratos")
          .select("importe_total, fecha_cierre, estatus")
          .eq("pais", pais)
          .eq("eliminada", false)
          .not("fecha_cierre", "is", null)
          .gte("fecha_cierre", meses90[0].desde)
          .lte("fecha_cierre", meses90[2].hasta),
      ]);

      const siiEm90 = siiEm90Res.data || [];
      const fp90 = fp90Res.data || [];
      // Combinar sales + sales_contratos en un solo pipeline
      const salesPipeline = (pipeline90Res.data || []).map((s) => ({
        importe: s.importe,
        fecha_cierre: s.fecha_cierre,
        estatus: s.estatus,
      }));
      const contratosPipeline = (pipelineContratos90Res.data || []).map((c) => ({
        importe: c.importe_total,
        fecha_cierre: c.fecha_cierre,
        estatus: c.estatus,
      }));
      const pipeline90 = [...salesPipeline, ...contratosPipeline].filter((s) => {
        const pct = parseInt(s.estatus, 10);
        return !Number.isNaN(pct) && pct >= 50;
      });

      let saldoAcumulado = saldoProyectado; // arranca desde proyección fin de mes actual
      const mesesData = meses90.map((mes) => {
        const enMes = (fecha) => fecha >= mes.desde && fecha <= mes.hasta;

        const ingSII = siiEm90
          .filter((f) => enMes(f.fecha_emision))
          .reduce((s, f) => s + Number(f.monto_total || 0), 0);

        const ingPipeline = pipeline90
          .filter((s) => {
            const fc = s.fecha_cierre?.substring(0, 10);
            return fc && enMes(fc);
          })
          .reduce((s, o) => s + Number(o.importe || 0), 0);

        const ingresos = ingSII + ingPipeline;

        const egresosFP = fp90
          .filter((f) => enMes(f.fecha))
          .reduce((s, f) => s + Math.max(0, Number(f.monto_total || 0) - Number(f.monto_pagado || 0)), 0);

        const egresos = egresosFP + gastosRecurrentes;

        saldoAcumulado = saldoAcumulado + ingresos - egresos;

        return {
          label: mes.label,
          ingresos,
          egresos,
          saldo: saldoAcumulado,
        };
      });

      setProyeccion90(mesesData);
    } catch (err) {
      console.error("Error cargando dashboard:", err);
      toast.error("Error cargando dashboard financiero");
    } finally {
      setLoading(false);
    }
  }, [pais]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // ─── Cálculos de saldos ────────────────────────────────

  const cuentaCLP = cuentas.find((c) => c.moneda === "CLP");
  const cuentaUSD = cuentas.find((c) => c.moneda === "USD");
  const saldoCLP = Number(cuentaCLP?.saldo_actual || 0);
  const saldoUSD = Number(cuentaUSD?.saldo_actual || 0);

  // Equivalente total en USD
  const saldoCLPenUSD = dolarHoy > 0 ? saldoCLP / dolarHoy : 0;
  const totalUSD = saldoUSD + saldoCLPenUSD;

  // Último sync: la fecha más reciente entre ambas cuentas
  const ultimoSync = [cuentaCLP?.saldo_fecha, cuentaUSD?.saldo_fecha].filter(Boolean).sort().pop();

  // ─── Render ────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <Header title="Dashboard Financiero" />
        <div className={styles.content}>
          <div className={styles.empty}>Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Header title="Dashboard Financiero" />

      <div className={styles.content}>
        {/* Sección A — Estado actual */}
        <div className={styles.seccion}>
          <div className={styles.seccionTitulo}>Estado actual</div>
          <div className={styles.saldosGrid}>
            <div className={styles.saldoCard}>
              <div className={styles.saldoLabel}>Saldo CLP</div>
              <div className={`${styles.saldoValor} ${saldoCLP >= 0 ? styles.saldoPositivo : styles.saldoNegativo}`}>
                {formatMonto(saldoCLP)}
              </div>
              <div className={styles.saldoMeta}>{cuentaCLP?.numero_cuenta || "Sin cuenta"}</div>
            </div>

            <div className={styles.saldoCard}>
              <div className={styles.saldoLabel}>Saldo USD</div>
              <div className={`${styles.saldoValor} ${saldoUSD >= 0 ? styles.saldoPositivo : styles.saldoNegativo}`}>
                {formatMonto(saldoUSD, "USD")}
              </div>
              <div className={styles.saldoMeta}>{cuentaUSD?.numero_cuenta || "Sin cuenta"}</div>
            </div>

            <div className={styles.saldoCard}>
              <div className={styles.saldoLabel}>Total equivalente USD</div>
              <div className={styles.saldoValor}>{formatMonto(totalUSD, "USD")}</div>
              <div className={styles.saldoMeta}>
                TC: ${dolarHoy.toLocaleString("es-CL")}/USD
                {dolarFecha && ` al ${dolarFecha.split("-").reverse().join("/")}`}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", fontSize: "0.68rem", color: "#bbb", marginTop: 8 }}>
            Último sync: {formatFechaHora(ultimoSync)} — Santander Chile
          </div>
        </div>

        {/* Alertas pendientes */}
        <div className={styles.seccion}>
          <div className={styles.seccionTitulo}>Pendientes</div>
          <div className={styles.alertasGrid}>
            <Link
              to="/admin/movimientos-bancarios"
              className={`${styles.alertaCard} ${alertas.movPendientes > 0 ? styles.alertaWarning : styles.alertaOk}`}
            >
              <div className={styles.alertaIcono}>{alertas.movPendientes > 0 ? "⚠" : "✓"}</div>
              <div className={styles.alertaTexto}>
                <div className={styles.alertaValor}>{alertas.movPendientes}</div>
                <div className={styles.alertaLabel}>Movimientos bancarios sin clasificar</div>
              </div>
            </Link>

            <Link
              to="/admin/conciliacion-sii"
              className={`${styles.alertaCard} ${alertas.dtesEmitidas > 0 ? styles.alertaWarning : styles.alertaOk}`}
            >
              <div className={styles.alertaIcono}>{alertas.dtesEmitidas > 0 ? "⚠" : "✓"}</div>
              <div className={styles.alertaTexto}>
                <div className={styles.alertaValor}>{alertas.dtesEmitidas}</div>
                <div className={styles.alertaLabel}>Facturas emitidas sin vincular</div>
              </div>
            </Link>

            <Link
              to="/admin/conciliacion-sii"
              className={`${styles.alertaCard} ${alertas.dtesRecibidas > 0 ? styles.alertaWarning : styles.alertaOk}`}
            >
              <div className={styles.alertaIcono}>{alertas.dtesRecibidas > 0 ? "⚠" : "✓"}</div>
              <div className={styles.alertaTexto}>
                <div className={styles.alertaValor}>{alertas.dtesRecibidas}</div>
                <div className={styles.alertaLabel}>Facturas recibidas sin asignar</div>
              </div>
            </Link>
          </div>
        </div>

        {/* Sección B — Proyección fin de mes */}
        {proyeccion && (
          <div className={styles.seccion}>
            <div className={styles.seccionTitulo}>Proyección fin de mes</div>
            <div className={styles.saldosGrid}>
              <div className={styles.saldoCard}>
                <div className={styles.saldoLabel}>Ingresos pendientes de cobro</div>
                <div className={`${styles.saldoValor} ${styles.saldoPositivo}`}>
                  {formatMonto(proyeccion.ingresosPendientes)}
                </div>
                <div className={styles.saldoMeta}>Facturado, no cobrado</div>
              </div>

              <div className={styles.saldoCard}>
                <div className={styles.saldoLabel}>Egresos comprometidos</div>
                <div className={`${styles.saldoValor} ${styles.saldoNegativo}`}>
                  {formatMonto(proyeccion.egresosComprometidos)}
                </div>
                <div className={styles.saldoMeta}>Facturas proveedor pendientes</div>
              </div>

              <div className={styles.saldoCard}>
                <div className={styles.saldoLabel}>Gastos recurrentes estimados</div>
                <div className={`${styles.saldoValor} ${styles.saldoNegativo}`}>
                  {formatMonto(proyeccion.gastosRecurrentes)}
                </div>
                <div className={styles.saldoMeta}>Promedio últimos 3 meses</div>
              </div>

              <div className={`${styles.saldoCard} ${styles.saldoProyectadoCard}`}>
                <div className={styles.saldoLabel}>Saldo proyectado</div>
                <div
                  className={`${styles.saldoValor} ${
                    proyeccion.saldoProyectado > 0 ? styles.saldoPositivo : styles.saldoNegativo
                  }`}
                >
                  {formatMonto(proyeccion.saldoProyectado)}
                </div>
                <div className={styles.saldoMeta}>
                  <span
                    className={`${styles.semaforo} ${
                      proyeccion.saldoProyectado < 0
                        ? styles.semaforoRojo
                        : proyeccion.saldoProyectado < saldoCLP * 0.2
                          ? styles.semaforoAmarillo
                          : styles.semaforoVerde
                    }`}
                  />
                  {proyeccion.saldoProyectado < 0
                    ? "Déficit proyectado"
                    : proyeccion.saldoProyectado < saldoCLP * 0.2
                      ? "Ajustado"
                      : "Holgura"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sección C — Proyección 90 días */}
        {proyeccion90 && (
          <div className={styles.seccion}>
            <div className={styles.seccionTitulo}>Proyección 90 días</div>
            <div className={styles.tabla90}>
              <table className={styles.table90}>
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Ingresos esperados</th>
                    <th>Egresos conocidos</th>
                    <th>Saldo acumulado</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {proyeccion90.map((mes) => (
                    <tr key={mes.label}>
                      <td style={{ fontWeight: 600 }}>{mes.label}</td>
                      <td className={styles.montoPositivo}>{formatMonto(mes.ingresos)}</td>
                      <td className={styles.montoNegativo}>{formatMonto(mes.egresos)}</td>
                      <td style={{ fontWeight: 700, color: mes.saldo >= 0 ? "#27ae60" : "#c0392b" }}>
                        {formatMonto(mes.saldo)}
                      </td>
                      <td>
                        <span
                          className={`${styles.semaforo} ${
                            mes.saldo < 0
                              ? styles.semaforoRojo
                              : mes.saldo < saldoCLP * 0.2
                                ? styles.semaforoAmarillo
                                : styles.semaforoVerde
                          }`}
                        />
                        {mes.saldo < 0 ? "Déficit" : mes.saldo < saldoCLP * 0.2 ? "Ajustado" : "Holgura"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardFinanciero;
